import React, { useContext, useState, useMemo } from 'react';
import { ThemeContext } from '../hooks/ThemeContext';
import { usePortfolioProducts } from '../hooks/useProducts';
import Watermark from '../components/common/Watermark';
import ProtectedImage from '../components/common/ProtectedImage';
import LoadingSpinner from '../components/globals/LoadingSpinner';
import PropTypes from 'prop-types';

const CreationsHero = ({ isDark, stats, products }) => (
  <div className={`${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-b border-white/10' : 'bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 border-b border-gray-200'}`}>
    <div className="container mx-auto px-4 py-16 lg:py-20 max-w-7xl">
      <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] items-start">
        <div className="space-y-6 lg:pr-8">
          <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-xl ${isDark ? 'border-white/10 bg-white/5 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`}>
            Portfolio visuel premium
          </div>

          <div className="space-y-4">
            <h1 className="text-4xl md:text-5xl xl:text-6xl font-bold leading-tight">
              Nos <span className="text-rose-500">Créations</span> en conditions réelles
            </h1>
            <p className={`max-w-2xl text-lg leading-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Découvrez une sélection claire de logos, bannières, contenus sociaux et assets digitaux conçus pour inspirer confiance dès le premier regard.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {stats.map((item) => (
              <div
                key={item.label}
                className={`rounded-3xl border p-5 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}
              >
                <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                  <span className="text-sm font-bold text-rose-400">{item.value}</span>
                </div>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{item.label}</p>
              </div>
            ))}
          </div>

          <div className={`rounded-3xl border p-6 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
            <p className={`text-base leading-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Pour acheter une création, rendez-vous sur notre <a href="/shop" className={`font-semibold underline underline-offset-4 ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>Boutique</a>. Cette page met uniquement en valeur le portfolio et la qualité visuelle du travail réalisé.
            </p>
          </div>
        </div>

        <PortfolioPreviewGrid isDark={isDark} products={products} />
      </div>
    </div>
  </div>
);

const PortfolioPreviewGrid = ({ isDark, products }) => (
  <div className={`rounded-3xl border p-6 md:p-8 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
    <div className="grid grid-cols-2 gap-4">
      {products.slice(0, 4).map((product, index) => {
        const preview = product.images.length > 0 ? product.images[0] : product.src;

        return (
          <div
            key={`${product.id}-${index}`}
            className={`overflow-hidden rounded-3xl border ${isDark ? 'border-white/10 bg-black/20' : 'border-gray-200 bg-slate-50'}`}
          >
            <div className={`h-40 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              {preview ? (
                <Watermark className="h-full w-full">
                  <ProtectedImage
                    imagePath={preview}
                    alt={product.title}
                    className="w-full h-full"
                    objectFit="cover"
                    showWatermark={true}
                  />
                </Watermark>
              ) : (
                <div className={`flex h-full items-center justify-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Visuel à venir
                </div>
              )}
            </div>
            <div className="p-4">
              <p className="text-sm font-semibold truncate">{product.title}</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{product.client}</p>
            </div>
          </div>
        );
      })}
      {products.length === 0 && (
        <div className={`col-span-2 rounded-3xl border p-8 text-center ${isDark ? 'border-white/10 bg-white/5 text-gray-300' : 'border-gray-200 bg-slate-50 text-gray-600'}`}>
          Le portfolio apparaîtra ici dès que des créations seront disponibles.
        </div>
      )}
    </div>
  </div>
);

const CreationFilters = ({ isDark, tabs, active, setActive }) => (
  <nav className={`mb-8 rounded-3xl border p-4 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`} aria-label="Filtres créations">
    <div className="flex flex-wrap gap-3">
      {tabs.map((tab) => {
        const inactiveClass = isDark
          ? 'bg-white/5 text-white border-white/10 hover:border-rose-500/40'
          : 'bg-white text-gray-800 border-gray-200 hover:border-rose-300';

        return (
          <button
            key={tab.key}
            onClick={() => setActive(tab.key)}
            className={`px-5 py-2.5 rounded-2xl text-sm transition border select-none shadow-xl ${active === tab.key ? 'bg-rose-600 text-white border-rose-600' : inactiveClass}`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  </nav>
);

const CreationCard = ({ isDark, product, cardBase }) => (
  <article className={`rounded-3xl overflow-hidden ${cardBase} transition hover:-translate-y-1 flex flex-col min-h-[360px]`}>
    <div className={`relative grid h-48 gap-1 md:h-56 ${product.images.length > 1 ? 'grid-cols-2 grid-rows-2' : 'grid-cols-1'} ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
      {(product.images.length > 0 ? product.images : [product.src]).filter(Boolean).slice(0, 4).map((image, index) => (
        <div key={`${product.id}-image-${index}`} className="relative overflow-hidden">
          <ProtectedImage
            imagePath={image}
            alt={`${product.title} ${index + 1}`}
            className="w-full h-full"
            objectFit="cover"
            showWatermark={true}
            hoverPreview={true}
            hoverPreviewScale={1.18}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        </div>
      ))}
      {!(product.images.length > 0 || product.src) && (
        <div className={`col-span-full row-span-full flex h-full w-full items-center justify-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          <span>Prévoir visuel ici</span>
        </div>
      )}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-4">
        <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'border border-white/10 bg-black/35 text-white' : 'border border-white/80 bg-white/85 text-gray-800'}`}>
          Portfolio
        </span>
        {product.images.length > 1 && (
          <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${isDark ? 'bg-rose-500/90 text-white' : 'bg-rose-600 text-white'}`}>
            {product.images.length} vues
          </span>
        )}
      </div>
    </div>

    <div className="p-5 flex flex-col flex-1 min-w-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-base md:text-lg leading-6">{product.title}</h3>
            <p className={`text-xs md:text-sm mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{product.client}</p>
          </div>
        </div>
        <div className="mt-3">
          <span className={`inline-flex max-w-full items-center justify-center break-words px-3 py-1 rounded-2xl text-[11px] leading-4 ${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 text-rose-300 border border-white/10' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
            {product.type}
          </span>
        </div>
      </div>
      <p className={`mt-4 text-sm leading-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        Aperçu éditorial de la création avec galerie intégrée et accès au détail produit.
      </p>
      <div className="flex-1" />
      <button
        className={`mt-5 px-4 py-3 rounded-2xl font-semibold transition-colors w-full shadow-xl ${isDark ? 'bg-white text-gray-900 hover:bg-rose-100' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
        onClick={() => {
          document.location.href = `/shop/${product.id}`;
        }}
      >
        Ouvrir la fiche
      </button>
    </div>
  </article>
);

const CreationsCta = ({ isDark }) => (
  <div className={`mt-12 rounded-3xl border p-8 md:p-10 text-center shadow-xl ${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-white/10' : 'bg-gradient-to-r from-rose-50 to-purple-50 border-gray-200'}`}>
    <h2 className="text-2xl md:text-3xl font-bold mb-4">Vous voulez aller plus loin ?</h2>
    <p className={`max-w-2xl mx-auto mb-6 leading-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
      Parcourez la boutique pour acheter une création ou contactez-nous pour demander un portfolio plus complet et adapté à votre projet.
    </p>
    <div className="flex flex-wrap items-center justify-center gap-4">
      <a href="/shop" className={`inline-block px-6 py-3 rounded-2xl font-semibold shadow-xl ${isDark ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-rose-600 text-white hover:bg-rose-700'}`}>
        Acheter des créations
      </a>
      <a href="/contact" className={`inline-block px-6 py-3 rounded-2xl font-semibold border shadow-xl ${isDark ? 'border-white/20 text-white bg-white/5 hover:bg-white/10' : 'border-gray-300 text-gray-800 bg-white hover:bg-gray-50'}`}>
        Demander un portfolio complet
      </a>
    </div>
  </div>
);

const creationShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  client: PropTypes.string.isRequired,
  src: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
});

CreationsHero.propTypes = {
  isDark: PropTypes.bool.isRequired,
  stats: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  })).isRequired,
  products: PropTypes.arrayOf(creationShape).isRequired,
};

PortfolioPreviewGrid.propTypes = {
  isDark: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(creationShape).isRequired,
};

CreationFilters.propTypes = {
  isDark: PropTypes.bool.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  active: PropTypes.string.isRequired,
  setActive: PropTypes.func.isRequired,
};

CreationCard.propTypes = {
  isDark: PropTypes.bool.isRequired,
  product: creationShape.isRequired,
  cardBase: PropTypes.string.isRequired,
};

CreationsCta.propTypes = {
  isDark: PropTypes.bool.isRequired,
};

const Creations = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const { products, loading, error } = usePortfolioProducts();
  const [active, setActive] = useState('all');

  const normalizedProducts = useMemo(() => (
    products.map((p) => ({
      id: p.id,
      type: (p.category || 'autre').toLowerCase(),
      title: p.name || p.title || 'Création',
      client: p.creator_name || 'Krayne Agency',
      src: p.image || null,
      images: Array.isArray(p.images) ? p.images.filter(Boolean).slice(0, 4) : [],
    }))
  ), [products]);

  const tabs = useMemo(() => {
    const categoryKeys = Array.from(new Set(normalizedProducts.map(p => p.type)));
    const dynamicTabs = categoryKeys.map(key => ({ key, label: key.charAt(0).toUpperCase() + key.slice(1) }));
    return [
      { key: 'all', label: 'Tout' },
      ...dynamicTabs,
    ];
  }, [normalizedProducts]);

  const filtered = useMemo(() => (
    active === 'all' ? normalizedProducts : normalizedProducts.filter(i => i.type === active)
  ), [active, normalizedProducts]);

  const stats = useMemo(() => ([
    { label: 'Créations visibles', value: normalizedProducts.length },
    { label: 'Catégories', value: Math.max(tabs.length - 1, 0) },
    { label: 'Sélection active', value: filtered.length },
  ]), [filtered.length, normalizedProducts.length, tabs.length]);

  const cardBase = isDark
    ? 'bg-white/5 border border-white/10 shadow-xl'
    : 'bg-white/95 border border-gray-200 shadow-xl';

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <CreationsHero isDark={isDark} stats={stats} products={normalizedProducts} />

      <div className="container mx-auto px-4 py-12 lg:py-16 max-w-7xl">

        {error && (
          <div className={`mb-8 rounded-3xl border px-5 py-4 shadow-xl ${isDark ? 'border-red-500/30 bg-red-500/10 text-red-200' : 'border-red-200 bg-red-50 text-red-600'}`}>{error}</div>
        )}

        {loading ? (
          <div className={`flex justify-center py-16 rounded-3xl border shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Filtres */}
            {tabs.length > 1 && (
              <CreationFilters isDark={isDark} tabs={tabs} active={active} setActive={setActive} />
            )}

            {/* Grille */}
            {filtered.length === 0 ? (
              <div className={`text-center py-14 text-sm rounded-3xl border shadow-xl ${isDark ? 'border-white/10 bg-white/5 text-gray-300' : 'border-gray-200 bg-white text-gray-600'}`}>
                Aucune création disponible pour le moment.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {filtered.map((p) => (
                  <CreationCard key={p.id} isDark={isDark} product={p} cardBase={cardBase} />
                ))}
              </div>
            )}
          </>
        )}

        {/* CTA */}
        <CreationsCta isDark={isDark} />
      </div>
    </div>
  );
};

export default Creations;
